/**
 * How a board applies a card's placement: as a removal from every cell the card is in that
 * it should no longer be in, and an addition to every cell it should be in and is not.
 *
 * **Never an atomic move.** A cell address is a pair, and a field on either axis can be
 * multi-valued, so one card can sit in several cells at once and a gesture can take it out
 * of one and into two. Removals and additions are sets, which makes a sideways drag, a
 * diagonal one and a multi-valued change the same three lines of arithmetic.
 *
 * The same function serves the pending overlay and the confirmed fold, because the two carry
 * the same kind of statement: these are the buckets the card is in now. That is what makes a
 * successful write settle without the card flickering back through where it came from.
 */

import type {BoardCell, BoardColumn, BoardData, BoardRow, CellAddress} from '@github-ui/board-core'
import type {DocumentAdapterResult, PendingConnectionOperation} from '@github-ui/optimistic-connection'

import {type BoardPlacement, readBoardPlacement} from './board-placement'
import type {SavedViewsBoardData, SavedViewsBoardItem} from './types'

type BoardItem = SavedViewsBoardItem
type BoardItemData = BoardData<BoardItem>
type BoardPayloadDocument = {boardData: SavedViewsBoardData<BoardItem>}

/**
 * Cells grouped by column, then row. A lookup reads both keys as they are, so nothing is
 * encoded per cell and no two addresses can collide.
 */
type CellSet = ReadonlyMap<string, ReadonlySet<string>>

function toCellSet(addresses: readonly CellAddress[]): CellSet {
  const set = new Map<string, Set<string>>()
  for (const {columnKey, rowKey} of addresses) {
    const rows = set.get(columnKey)
    if (rows === undefined) set.set(columnKey, new Set([rowKey]))
    else rows.add(rowKey)
  }
  return set
}

function hasCell(set: CellSet, {columnKey, rowKey}: CellAddress): boolean {
  return set.get(columnKey)?.has(rowKey) ?? false
}

export function isBoardPayloadDocument(value: unknown): value is BoardPayloadDocument {
  if (value == null || typeof value !== 'object') return false
  const {boardData} = value as {boardData?: Partial<SavedViewsBoardData<BoardItem>>}
  if (boardData == null || typeof boardData !== 'object') return false

  const {data} = boardData
  return data != null && Array.isArray(data.cells) && Array.isArray(data.columns) && Array.isArray(data.rows)
}

/**
 * Applies a board placement to whichever document was handed to it.
 *
 * Declines anything that is not a placement, including every other kind of field edit: a
 * board card's own fields come from the next board read, and an operation this board cannot
 * place is one it has nothing to say about.
 */
export function applyBoardPlacement<T>(document: T, change: PendingConnectionOperation): DocumentAdapterResult<T> {
  const {operation} = change
  if (operation.type !== 'setFields') return undefined

  const placement = readBoardPlacement(operation)
  if (placement === undefined || !isBoardPayloadDocument(document)) return undefined

  const boardData = placeCard(document.boardData, operation.id, placement)
  if (boardData === undefined) return undefined
  if (boardData === document.boardData) return {document}

  return {document: {...document, boardData}}
}

export const boardDocumentAdapter = applyBoardPlacement

function placeCard(
  boardData: SavedViewsBoardData<BoardItem>,
  itemId: string,
  placement: BoardPlacement,
): SavedViewsBoardData<BoardItem> | undefined {
  const {columnField, rowField, data} = boardData
  // A board with no grouping has no cells to place a card in (D29).
  if (columnField == null) return undefined

  const placedColumns = placement.axes[columnField]?.keys
  const placedRows = rowField == null ? undefined : placement.axes[rowField]?.keys
  // An operation naming neither of this board's axes is about a board arranged by other
  // fields, which is not a placement this one can read.
  if (placedColumns === undefined && placedRows === undefined) return undefined

  const {card, held} = findCard(data, itemId)
  // The card is not on this board. A placement cannot be applied to a cell nobody is
  // holding, and guessing one from the operation would invent a card the read never sent.
  if (card === undefined) return undefined

  // An axis the operation does not name is left where it is. That is how a sideways drag
  // settles: the write reports the column it changed, and the row comes from the board.
  const columnKeys = placedColumns ?? distinct(held.map(cell => cell.columnKey))
  const rowKeys = placedRows ?? distinct(held.map(cell => cell.rowKey))

  const destination = columnKeys.flatMap(columnKey => rowKeys.map(rowKey => ({columnKey, rowKey})))
  const destinationSet = toCellSet(destination)
  const heldSet = toCellSet(held)

  // `held` only sees loaded cards. A card already on an unfetched page of a destination cell
  // is counted twice until the next board read. A stale index looks the same and needs the count.
  const removals = held.filter(cell => !hasCell(destinationSet, cell))
  const additions = destination.filter(address => !hasCell(heldSet, address))
  // Already where the placement says it goes. Affected, and nothing to change.
  if (removals.length === 0 && additions.length === 0) return boardData

  return {
    ...boardData,
    data: {
      ...data,
      cells: nextCells(data, {itemId, card, removals, additions}),
      // A column shows the count it was given, so the count moves with the card rather than
      // being re-derived from the cells now under it.
      columns: withAxisCounts(
        data.columns,
        axisDelta(
          held.map(cell => cell.columnKey),
          columnKeys,
        ),
      ),
      rows: withAxisCounts(
        data.rows,
        axisDelta(
          held.map(cell => cell.rowKey),
          rowKeys,
        ),
      ),
    },
  }
}

/** The card, and every cell currently holding it. A multi-valued axis holds it in several. */
function findCard(data: BoardItemData, itemId: string): {card?: BoardItem; held: CellAddress[]} {
  let card: BoardItem | undefined
  const held: CellAddress[] = []

  for (const cell of data.cells) {
    const found = cell.items.find(item => item.globalId === itemId)
    if (found === undefined) continue
    card ??= found
    held.push({columnKey: cell.columnKey, rowKey: cell.rowKey})
  }

  return {card, held}
}

/**
 * Every cell the board is currently drawing one card in.
 *
 * Read when a gesture is turned into an operation, because what the card holds on the axis it
 * did not cross has to survive the move, and only the board knows it.
 */
export function heldCells(boardData: SavedViewsBoardData<BoardItem>, itemId: string): CellAddress[] {
  return findCard(boardData.data, itemId).held
}

function nextCells(
  {cells, columns, rows}: BoardItemData,
  {
    itemId,
    card,
    removals,
    additions,
  }: {itemId: string; card: BoardItem; removals: readonly CellAddress[]; additions: readonly CellAddress[]},
): Array<BoardCell<BoardItem>> {
  // Runs over every cell on each drop, so a cell the move does not touch costs two `Map`
  // reads and allocates nothing.
  const removed = toCellSet(removals)
  const added = toCellSet(additions)
  const sent: CellAddress[] = []

  const next = cells.map(cell => {
    if (hasCell(removed, cell)) return withoutCard(cell, itemId)
    if (!hasCell(added, cell)) return cell
    sent.push(cell)
    return withCard(cell, card)
  })

  // A cell the read payload never sent is an empty pair, so the card arrives in a cell of
  // its own. Only on a loaded column and row: the board draws no other cell, and a count of
  // one there would be a number the board made up.
  const sentSet = toCellSet(sent)
  for (const address of additions) {
    if (hasCell(sentSet, address)) continue
    if (!columns.some(column => column.key === address.columnKey)) continue
    if (!rows.some(row => row.key === address.rowKey)) continue
    next.push({...address, items: [card], totalCount: 1, hasMoreItems: false})
  }

  return next
}

function withoutCard(cell: BoardCell<BoardItem>, itemId: string): BoardCell<BoardItem> {
  const items = cell.items.filter(item => item.globalId !== itemId)
  if (items.length === cell.items.length) return cell

  return {...cell, items, totalCount: decrement(cell.totalCount)}
}

function withCard(cell: BoardCell<BoardItem>, card: BoardItem): BoardCell<BoardItem> {
  if (cell.items.some(item => item.globalId === card.globalId)) return cell

  // At the top of the cell rather than the bottom. A cell pages, so its end sits below cards
  // nobody has fetched, and the top is the one position a reader is certain to see the card
  // land in. The next board read puts it where the view's sort says it belongs.
  return {...cell, items: [card, ...cell.items], totalCount: increment(cell.totalCount)}
}

/** What one axis's counts owe the move: a card leaving a bucket, or arriving in one. */
function axisDelta(before: readonly string[], after: readonly string[]): ReadonlyMap<string, number> {
  const held = new Set(before)
  const placed = new Set(after)
  const delta = new Map<string, number>()

  for (const key of held) if (!placed.has(key)) delta.set(key, -1)
  for (const key of placed) if (!held.has(key)) delta.set(key, 1)

  return delta
}

function withAxisCounts<TEntry extends BoardColumn | BoardRow>(
  axis: readonly TEntry[],
  delta: ReadonlyMap<string, number>,
): readonly TEntry[] {
  if (delta.size === 0) return axis

  let changed = false
  const next = axis.map(entry => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- `key` here is the axis entry's wire-contract key, not a keyboard event's
    const step = delta.get(entry.key)
    if (step === undefined || entry.totalCount === undefined) return entry
    changed = true
    return {...entry, totalCount: step < 0 ? decrement(entry.totalCount) : increment(entry.totalCount)}
  })

  return changed ? next : axis
}

/** A count nobody stated stays unstated rather than becoming one the board invented. */
function increment(totalCount: number | undefined): number | undefined {
  return totalCount === undefined ? undefined : totalCount + 1
}

function decrement(totalCount: number | undefined): number | undefined {
  return totalCount === undefined ? undefined : Math.max(0, totalCount - 1)
}

function distinct(keys: readonly string[]): string[] {
  return [...new Set(keys)]
}
